import React, { useState } from 'react';
import { X, Save } from 'lucide-react';
import { API_BASE_URL } from '../../config';
import ContextHelp from '../ContextHelp';
import './ContextCredentialModal.css';

const ProviderLogo = ({ provider, size = 20, className = "" }) => {
    const logos = {
        aws: 'https://www.vectorlogo.zone/logos/amazon_aws/amazon_aws-icon.svg',
        gcp: 'https://www.vectorlogo.zone/logos/google_cloud/google_cloud-icon.svg',
        azure: 'https://www.vectorlogo.zone/logos/microsoft_azure/microsoft_azure-icon.svg'
    };
    return (
        <img
            src={logos[provider]}
            alt={provider.toUpperCase()}
            className={`${className} ${provider === 'aws' ? 'aws-logo-theme-fix' : ''}`}
            style={{ width: provider === 'aws' ? Math.round(size * 1.6) : size, height: size, objectFit: 'contain', verticalAlign: 'middle' }}
        />
    );
};

export default function ContextCredentialModal({ provider, onClose, onSaved }) {
    const [name, setName] = useState('');
    const [awsAccessKey, setAwsAccessKey] = useState('');
    const [awsSecretKey, setAwsSecretKey] = useState('');
    const [gcpProjectId, setGcpProjectId] = useState('');
    const [gcpJson, setGcpJson] = useState('');
    const [azureTenant, setAzureTenant] = useState('');
    const [azureClient, setAzureClient] = useState('');
    const [azureSecret, setAzureSecret] = useState('');
    const [azureSub, setAzureSub] = useState('');
    const [saving, setSaving] = useState(false);

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        let credentialsObj = {};
        if (provider === 'aws') {
            credentialsObj = { accessKeyId: awsAccessKey, secretAccessKey: awsSecretKey };
        } else if (provider === 'gcp') {
            try { 
                credentialsObj = JSON.parse(gcpJson); 
                if (gcpProjectId) credentialsObj.project_id = gcpProjectId;
            } catch { 
                alert('Invalid GCP JSON'); 
                setSaving(false);
                return; 
            }
        } else if (provider === 'azure') {
            credentialsObj = { tenantId: azureTenant, clientId: azureClient, clientSecret: azureSecret, subscriptionId: azureSub };
        }
        
        try {
            const res = await fetch(`${API_BASE_URL}/api/credentials`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ provider, name, credentials: credentialsObj }),
            });
            const data = await res.json();
            if (data.success) { 
                onSaved(data.data.id);
            } else {
                alert(data.error || 'Error saving credential');
            }
        } catch (e) {
            console.error('Failed to save', e);
            alert('Failed to save credential due to network error');
        } finally {
            setSaving(false);
        }
    };

    const renderLabel = (txt, fieldName) => {
        return (
            <label style={{ display: 'flex', alignItems: 'center', fontSize: '0.85rem', marginBottom: '6px', fontWeight: 500, color: 'var(--text-primary)' }}>
                <span>{txt}</span>
                {fieldName && <ContextHelp field={fieldName} provider={provider} />}
            </label>
        );
    };

    const providerNameMap = {
        aws: 'AWS',
        gcp: 'Google Cloud',
        azure: 'Azure'
    };
    const providerName = providerNameMap[provider] || provider.toUpperCase();

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'var(--modal-overlay, rgba(0,0,0,0.6))', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
        }} onClick={onClose}>
            <div className="credential-modal-content" style={{
                width: '100%', maxWidth: '550px',
                borderRadius: '16px', 
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
                border: '1px solid var(--border-color)',
                overflow: 'hidden',
                animation: 'slideUp 0.3s ease-out'
            }} onClick={e => e.stopPropagation()}>
                <div style={{
                    padding: '1.25rem 1.5rem',
                    borderBottom: '1px solid var(--border-color)',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                            background: 'var(--bg-button-secondary)',
                            padding: '8px',
                            borderRadius: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <ProviderLogo provider={provider} size={24} />
                        </div>
                        <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                            Create {providerName} Credential
                        </h3>
                    </div>
                    <button type="button" onClick={onClose} style={{
                        background: 'transparent', border: 'none', color: 'var(--text-muted)',
                        cursor: 'pointer', padding: '6px', borderRadius: '6px', display: 'flex'
                    }}>
                        <X size={20} />
                    </button>
                </div>

                <div style={{ padding: '1.5rem' }}>
                    <form onSubmit={handleSave}>
                        <div style={{ marginBottom: '1.25rem' }}>
                            {renderLabel('Credential Name', 'Friendly Name')}
                            <input required value={name} onChange={e => setName(e.target.value)}
                                placeholder={`e.g. Production ${providerName}`} className="form-control" style={{ width: '100%' }} />
                        </div>

                        {provider === 'aws' && (
                            <>
                                <div style={{ marginBottom: '1.25rem' }}>
                                    {renderLabel('Access Key ID', 'Access Key ID')}
                                    <input required value={awsAccessKey} onChange={e => setAwsAccessKey(e.target.value)}
                                        placeholder="AKIA..." className="form-control" style={{ fontFamily: 'monospace', width: '100%' }} />
                                </div>
                                <div style={{ marginBottom: '1.25rem' }}>
                                    {renderLabel('Secret Access Key', 'Secret Access Key')}
                                    <input required type="password" value={awsSecretKey} onChange={e => setAwsSecretKey(e.target.value)}
                                        placeholder="••••••••••" className="form-control" style={{ width: '100%' }} />
                                </div>
                            </>
                        )}

                        {provider === 'gcp' && (
                            <>
                                <div style={{ marginBottom: '1.25rem' }}>
                                    {renderLabel('Project ID', 'Project ID')}
                                    <input required value={gcpProjectId} onChange={e => setGcpProjectId(e.target.value)}
                                        placeholder="my-gcp-project" className="form-control" style={{ fontFamily: 'monospace', width: '100%' }} />
                                </div>
                                <div style={{ marginBottom: '1.25rem' }}>
                                    {renderLabel('Service Account Key (JSON)', 'Service Account Key')}
                                    <textarea required rows={5} value={gcpJson} onChange={e => setGcpJson(e.target.value)}
                                        placeholder='{"type":"service_account",...}' className="form-control"
                                        style={{ fontFamily: 'monospace', resize: 'vertical', width: '100%' }} />
                                </div>
                            </>
                        )}

                        {provider === 'azure' && (
                            <>
                                <div style={{ marginBottom: '1.25rem' }}>
                                    {renderLabel('Tenant ID', 'Tenant ID')}
                                    <input required value={azureTenant} onChange={e => setAzureTenant(e.target.value)}
                                        placeholder="xxxxxxxx-xxxx-xxxx" className="form-control" style={{ fontFamily: 'monospace', width: '100%' }} />
                                </div>
                                <div style={{ marginBottom: '1.25rem' }}>
                                    {renderLabel('Client ID', 'Client ID')}
                                    <input required value={azureClient} onChange={e => setAzureClient(e.target.value)}
                                        placeholder="xxxxxxxx-xxxx-xxxx" className="form-control" style={{ fontFamily: 'monospace', width: '100%' }} />
                                </div>
                                <div style={{ marginBottom: '1.25rem' }}>
                                    {renderLabel('Client Secret', 'Client Secret')}
                                    <input required type="password" value={azureSecret} onChange={e => setAzureSecret(e.target.value)}
                                        placeholder="••••••••••" className="form-control" style={{ width: '100%' }} />
                                </div>
                                <div style={{ marginBottom: '1.25rem' }}>
                                    {renderLabel('Subscription ID', 'Subscription ID')}
                                    <input required value={azureSub} onChange={e => setAzureSub(e.target.value)}
                                        placeholder="xxxxxxxx-xxxx-xxxx" className="form-control" style={{ fontFamily: 'monospace', width: '100%' }} />
                                </div>
                            </>
                        )}

                        <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem', justifyContent: 'flex-end' }}>
                            <button type="button" onClick={onClose} className="btn-secondary" disabled={saving}>
                                Cancel
                            </button>
                            <button type="submit" className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '8px' }} disabled={saving}>
                                {saving ? 'Saving...' : <><Save size={16} /> Save Credential</>}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
            <style>{`
                @keyframes slideUp {
                    from { transform: translateY(20px); opacity: 0; }
                    to { transform: translateY(0); opacity: 1; }
                }
            `}</style>
        </div>
    );
}
